import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DatumFeld from '../components/DatumFeld';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Plus, Edit2, AlertTriangle, Timer, Save, Truck, User } from 'lucide-react';
import { api, extractError } from '../lib/api';
import { fmtDate, fmtDateTime } from '../lib/date';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Woher kam der Aufruf? Die Plantafel gibt das beim Navigieren mit.
  // Ohne Angabe bleibt es bei der Projektliste – der übliche Weg hierher.
  const herkunft = location.state?.herkunft;
  const herkunftPlantafel = herkunft === '/plantafel';
  const zurueck = () => navigate(herkunft || '/projekte');
  const [project, setProject] = useState(null);
  const [costCenters, setCostCenters] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [devices, setDevices] = useState([]);
  const [deviceAssignments, setDeviceAssignments] = useState([]);
  const [hoursSummary, setHoursSummary] = useState(null);
  const [extendOpen, setExtendOpen] = useState(false);
  const [newEnd, setNewEnd] = useState('');
  const [reason, setReason] = useState('');
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetValue, setBudgetValue] = useState('');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const load = async () => {
    const [p, cc, ee, hs, dv, da] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get('/cost-centers', { params: { include_inactive: true } }),
      api.get('/employees', { params: { include_inactive: true } }),
      api.get(`/projects/${projectId}/hours-summary`),
      api.get('/devices', { params: { include_inactive: true } }),
      api.get(`/projects/${projectId}/device-assignments`),
    ]);
    setProject(p.data); setCostCenters(cc.data); setEmployees(ee.data); setHoursSummary(hs.data);
    setDevices(dv.data); setDeviceAssignments(da.data);
    const a = await api.get('/assignments');
    setAssignments(a.data.filter(x => x.project_id === projectId));
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [projectId]);

  const extend = async () => {
    if (!newEnd || !reason) { toast.error('Bitte neues Enddatum und Grund angeben.'); return; }
    try {
      await api.post(`/projects/${projectId}/extend`, { new_end_date: newEnd, reason });
      toast.success('Projekt verlängert.');
      setExtendOpen(false); setNewEnd(''); setReason('');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const saveBudget = async () => {
    const val = budgetValue.trim() === '' ? null : Number(String(budgetValue).replace(',', '.'));
    if (val !== null && (isNaN(val) || val < 0)) { toast.error('Ungültige Stundenzahl.'); return; }
    try {
      await api.patch(`/projects/${projectId}`, { budget_hours: val });
      toast.success('Budget aktualisiert.');
      setBudgetOpen(false);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const submitAdjustment = async () => {
    const delta = Number(String(adjustDelta).replace(',', '.'));
    if (isNaN(delta) || delta === 0) { toast.error('Bitte gültige Stundenänderung angeben (z.B. -2 oder 5).'); return; }
    if (!adjustNote.trim()) { toast.error('Bitte Notiz zur Anpassung angeben.'); return; }
    try {
      await api.post(`/projects/${projectId}/hour-adjustment`, { delta_hours: delta, note: adjustNote.trim() });
      toast.success('Anpassung gespeichert.');
      setAdjustOpen(false); setAdjustDelta(''); setAdjustNote('');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  if (!project) return <div className="p-6 text-slate-500">Lädt…</div>;
  const kst = costCenters.find(c => c.id === project.cost_center_id);
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = project.status === 'Aktiv' && project.current_end_date < today;
  const totalDelay = Math.round((new Date(project.current_end_date) - new Date(project.original_end_date)) / 86400000);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={zurueck} data-testid="project-back">
          <ArrowLeft size={14} className="mr-1"/>{herkunftPlantafel ? 'Zurück zur Plantafel' : 'Zurück'}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                {project.project_number && <Badge className="bg-slate-900 text-white hover:bg-slate-900 font-mono">#{project.project_number}</Badge>}
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  {project.name}
                  {(project.extensions || []).length > 0 && <Badge variant="outline"><Clock size={12} className="mr-1"/>verlängert</Badge>}
                  {isOverdue && <Badge className="bg-rose-700 text-white hover:bg-rose-700"><AlertTriangle size={12} className="mr-1"/>überfällig</Badge>}
                </CardTitle>
              </div>
              <div className="text-sm text-slate-500 mt-1">Kunde: <span className="font-semibold text-slate-700">{project.customer}</span> – {project.location}</div>
            </div>
            <Button onClick={() => { setNewEnd(''); setReason(''); setExtendOpen(true); }} data-testid="extend-project-button"><Clock size={14} className="mr-1"/>Enddatum verlängern</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <InfoField label="Start" value={fmtDate(project.start_date)} />
            <InfoField label="Ursprünglich geplant bis" value={fmtDate(project.original_end_date)} />
            <InfoField label="Aktuell geplant bis" value={fmtDate(project.current_end_date)} highlight={totalDelay > 0} />
            <InfoField label="Gesamtverzögerung" value={`${totalDelay} Tage`} highlight={totalDelay > 0} />
            <InfoField label="Kostenstelle" value={kst?.name || '-'} />
            <InfoField label="Status" value={project.status} />
          </div>
          {project.description && <div className="mt-4 text-sm p-3 rounded-md bg-slate-50 border border-slate-200 whitespace-pre-wrap">{project.description}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-display text-base flex items-center gap-2"><Timer size={16}/>Stundenbudget</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setBudgetValue(project.budget_hours ?? ''); setBudgetOpen(true); }} data-testid="edit-budget-button">
                <Edit2 size={12} className="mr-1"/>Budget bearbeiten
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setAdjustDelta(''); setAdjustNote(''); setAdjustOpen(true); }} data-testid="add-adjustment-button">
                <Plus size={12} className="mr-1"/>Stunden-Anpassung
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {hoursSummary ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <InfoField label="Budget" value={hoursSummary.budget_hours != null ? `${hoursSummary.budget_hours}h` : '—'} />
                <InfoField label="Verplant" value={`${hoursSummary.planned_hours}h`} />
                <InfoField label="Manuelle Anpassung" value={`${hoursSummary.adjustments_total >= 0 ? '+' : ''}${hoursSummary.adjustments_total}h`} highlight={hoursSummary.adjustments_total !== 0} />
                <InfoField label="Verbraucht" value={`${hoursSummary.consumed_hours}h`} highlight={hoursSummary.is_over_budget} />
              </div>
              {hoursSummary.budget_hours != null && hoursSummary.budget_hours > 0 && (
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-500 font-mono">Fortschritt</span>
                    <span className={`font-mono font-semibold ${hoursSummary.is_over_budget ? 'text-rose-700' : hoursSummary.utilization_pct >= 80 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {hoursSummary.utilization_pct}%
                    </span>
                  </div>
                  <div className="relative h-2.5 rounded bg-slate-100 overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${hoursSummary.is_over_budget ? 'bg-rose-600' : hoursSummary.utilization_pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, hoursSummary.utilization_pct)}%` }}
                    />
                  </div>
                  {hoursSummary.is_over_budget && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-700 font-semibold">
                      <AlertTriangle size={12}/>
                      Budget überzogen um {(hoursSummary.consumed_hours - hoursSummary.budget_hours).toFixed(2).replace('.', ',')}h
                    </div>
                  )}
                  {!hoursSummary.is_over_budget && hoursSummary.remaining_hours != null && (
                    <div className="mt-2 text-xs text-slate-500">
                      Verbleibend: <span className="font-mono font-semibold text-slate-800">{hoursSummary.remaining_hours}h</span>
                    </div>
                  )}
                </div>
              )}

              {(project.hour_adjustments || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-2">Manuelle Anpassungen ({(project.hour_adjustments || []).length})</div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Datum</TableHead><TableHead>Änderung</TableHead><TableHead>Grund</TableHead><TableHead>Von</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(project.hour_adjustments || []).sort((a,b) => (b.timestamp || '').localeCompare(a.timestamp || '')).map(adj => (
                        <TableRow key={adj.id}>
                          <TableCell className="font-mono text-xs">{fmtDateTime(adj.timestamp)}</TableCell>
                          <TableCell className={`font-mono font-semibold ${adj.delta_hours < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                            {adj.delta_hours > 0 ? '+' : ''}{adj.delta_hours}h
                          </TableCell>
                          <TableCell className="text-sm">{adj.note}</TableCell>
                          <TableCell className="text-xs text-slate-600">{adj.by_user || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500">Lädt Stundendaten…</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-display text-base flex items-center gap-2"><Clock size={16}/>Verlängerungshistorie</CardTitle></CardHeader>
        <CardContent>
          {(project.extensions || []).length === 0 ? (
            <div className="text-sm text-slate-500">Keine Verlängerungen vorhanden.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Eingetragen</TableHead><TableHead>Altes Ende</TableHead><TableHead>Neues Ende</TableHead><TableHead>Grund</TableHead><TableHead>Von</TableHead></TableRow></TableHeader>
              <TableBody>
                {(project.extensions || []).map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono text-xs">{fmtDateTime(e.timestamp)}</TableCell>
                    <TableCell className="font-mono text-sm">{fmtDate(e.old_end_date)}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{fmtDate(e.new_end_date)}</TableCell>
                    <TableCell>{e.reason}</TableCell>
                    <TableCell className="text-xs text-slate-600">{e.by_user || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Truck size={16}/>Geräte-Einsätze ({deviceAssignments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deviceAssignments.length === 0 ? (
            <div className="text-sm text-slate-500">Keine Geräte für dieses Projekt geplant.</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Gerät</TableHead>
                <TableHead>Fahrer</TableHead>
                <TableHead className="text-right">Stunden</TableHead>
                <TableHead>Notiz</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {deviceAssignments.sort((a,b) => a.date.localeCompare(b.date)).map(da => {
                  const dev = devices.find(x => x.id === da.device_id);
                  const usedDriverId = da.driver_employee_id || dev?.default_driver_employee_id;
                  const driver = usedDriverId ? employees.find(e => e.id === usedDriverId) : null;
                  return (
                    <TableRow key={da.id} data-testid={`project-device-row-${da.id}`}>
                      <TableCell className="font-mono text-sm">{fmtDate(da.date)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: dev?.color || '#64748b' }}/>
                          <span className="font-semibold">{dev?.name || '(unbekannt)'}</span>
                          {dev?.inventory_number && <Badge variant="outline" className="font-mono text-[10px]">{dev.inventory_number}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {driver ? (
                          <span className="flex items-center gap-1 text-sm">
                            <User size={12} className="text-slate-500"/>
                            {driver.first_name} {driver.last_name}
                            {!da.driver_employee_id && <Badge variant="outline" className="text-[9px] ml-1">Standard</Badge>}
                          </span>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                            <AlertTriangle size={9} className="mr-0.5"/>ohne Fahrer
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{typeof da.hours === 'number' ? `${da.hours}h`.replace('.', ',') : '8h'}</TableCell>
                      <TableCell className="text-xs text-slate-600">{da.notes || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-display text-base">Einsätze in diesem Projekt ({assignments.length})</CardTitle></CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <div className="text-sm text-slate-500">Keine Einsätze.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Datum</TableHead><TableHead>Mitarbeiter</TableHead><TableHead>Stunden</TableHead><TableHead>Ausleihe</TableHead></TableRow></TableHeader>
              <TableBody>
                {assignments.sort((a,b) => a.date.localeCompare(b.date)).map(a => {
                  const e = employees.find(x => x.id === a.employee_id);
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-sm">{fmtDate(a.date)}</TableCell>
                      <TableCell>{e ? `${e.first_name} ${e.last_name}` : '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{typeof a.hours === 'number' ? `${a.hours}h`.replace('.', ',') : '8h'}</TableCell>
                      <TableCell>{a.lending_type === 'schabos' ? <Badge>Schabos</Badge> : a.lending_type === 'internal' ? <Badge variant="outline">Intern</Badge> : '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Projekt verlängern</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">Aktuelles Enddatum: <span className="font-mono font-semibold">{fmtDate(project.current_end_date)}</span></div>
            <div>
              <Label>Neues Enddatum</Label>
              <DatumFeld value={newEnd} onChange={setNewEnd} testId="extend-new-date"/>
            </div>
            <div><Label>Grund der Verlängerung</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="z.B. Lieferverzug Material" data-testid="extend-reason"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendOpen(false)}>Abbrechen</Button>
            <Button onClick={extend} data-testid="extend-submit-button">Verlängern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stundenbudget bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Gebuchte Stunden</Label>
              <Input type="number" step="0.5" min="0" value={budgetValue} onChange={e => setBudgetValue(e.target.value)} placeholder="z.B. 120" data-testid="budget-hours-input"/>
              <p className="text-xs text-slate-500 mt-1">Leer lassen, um kein Budget zu hinterlegen.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBudgetOpen(false)}>Abbrechen</Button>
            <Button onClick={saveBudget} data-testid="budget-save-button"><Save size={12} className="mr-1"/>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manuelle Stunden-Anpassung</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Änderung in Stunden (+ oder -)</Label>
              <Input type="number" step="0.25" value={adjustDelta} onChange={e => setAdjustDelta(e.target.value)} placeholder="z.B. -2 oder 5" data-testid="adjust-delta-input"/>
              <p className="text-xs text-slate-500 mt-1">Positive Zahl = mehr Stunden, Negative = Abzug/Korrektur.</p>
            </div>
            <div>
              <Label>Grund/Notiz (Pflicht)</Label>
              <Textarea value={adjustNote} onChange={e => setAdjustNote(e.target.value)} rows={3} placeholder="z.B. Falsche Buchung, Materialproblem etc." data-testid="adjust-note-input"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Abbrechen</Button>
            <Button onClick={submitAdjustment} data-testid="adjust-submit-button"><Save size={12} className="mr-1"/>Anpassung speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoField({ label, value, highlight }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`font-semibold ${highlight ? 'text-amber-700' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}
